import { resourceTypes, type SavedFilterType } from "../FindingsUtils";
import { getIconByType } from "../../Components/Tree/TreeUtils";
import { statusCategories } from "../FindingDetailsPane/FindingDetailsPaneHeader/Statuses/StatusUtils";
import { unescape } from "lodash-es";
import type {
  FilterPanel,
  FilterPinItem,
} from "../../AppView/FilterManagement/CommonFilterTypes";
import type {
  ScopeNode,
  ScopeType,
  SelectedScope,
} from "../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";

export const DEFAULT_FILTER = "ALL_FILTERS";
export const DEFAULT_SCOPE = "ALL_SCOPES";
export const SYSTEM = "seemplicity";
export const DEFAULT_SCOPE_NAME = "All Resources";
export const DEFAULT_FILTER_NAME = "Open Findings";
export const LS_DV_FILTER = "data-validation-findings-filter";
export const LS_FILTER = "findings-filter";
export const LS_REMEDIATION_ITEMS_FILTER = "remediation-items-filter";
export const LS_SF_FILTER = "seemplicity-findings-filter";
export const LS_AUTOMATION_FILTER = "automation-wizard-filter";
export const LS_RESOURCES_FILTER = "resources-filter";
export const LS_SCOPE = "findings-scope";
export const LS_RECENT_FILTERS = "recent-filters";
export const RECENT_LIST_SIZE = 10;
export const MIN_CHARS_FOR_FILTER_QUERY = 3;
export const MAX_RESULTS_TO_SHOW = 3;
export const MAX_SEARCH_RESULTS_COUNT = 100;
export const UNSAVED_SCOPE = "Unsaved Scope";
export const UNSAVED_FILTER = "Unsaved Filter";
export const STATUS_FILTER_TO_PIN: FilterPinItem = {
  filterField: "actual_status",
  label: "Status",
};

// Tree node walked by `getAllGroups`. Mirrors the scope-hierarchy node shape:
// each node may have a type/id/name and recursive children.
interface GroupTreeNode {
  type?: string;
  id?: string;
  name?: string;
  children?: GroupTreeNode[];
}

interface GroupOption {
  label?: string;
  value?: string;
  icon: string;
}

export const getAllGroups = (
  node: GroupTreeNode | null | undefined,
  groupId: string | undefined,
  acc: GroupOption[] = []
): GroupOption[] => {
  if (!node) {
    return [];
  }

  if (node.type === resourceTypes.GROUP && node.id !== groupId) {
    acc.push({
      label: node.name,
      value: node.id,
      icon: getIconByType(node.type),
    });
  }

  if (node?.children?.length) {
    for (let i = 0; i < node?.children?.length; i++) {
      getAllGroups(node.children[i], groupId, acc);
    }
  }

  return acc;
};

// GraphQL `finding_filters` edge node consumed by `filtersDataToFiltersList`.
interface FindingFilterNode {
  name: string;
  filter_data: string;
  id: string;
  editable: boolean;
  system: boolean;
  popular: boolean;
  default: boolean;
  created_by: string;
  filter_type: string;
}

interface FindingFiltersData {
  finding_filters: {
    edges: Array<{ node: FindingFilterNode }>;
  };
}

interface FilterListOption {
  label: string;
  value: {
    query: string;
    id: string;
    editable: boolean;
  };
  meta: {
    system: boolean;
    popular: boolean;
    default: boolean;
    created_by: string;
  };
}

export const filtersDataToFiltersList = (
  filtersData: FindingFiltersData,
  savedFilterType: SavedFilterType
): [Array<FilterListOption | null>, FilterListOption | null] => {
  let findingsFilterData: Array<FilterListOption | null> = [];

  let FILTER_ALL_OPEN: FilterListOption | null = null;
  filtersData.finding_filters.edges.forEach((item) => {
    const optionValue: FilterListOption = {
      label: unescape(item.node.name),
      value: {
        query: item.node.filter_data,
        id: item.node.id,
        editable: item.node.editable,
      },
      meta: {
        system: item.node.system,
        popular: item.node.popular,
        default: item.node.default,
        created_by: item.node.created_by,
      },
    };
    // Safety net: the server filters by type; only offer rows of the requested type.
    if (item.node.filter_type === savedFilterType) {
      if (
        item.node.name === DEFAULT_FILTER &&
        item.node.created_by === SYSTEM
      ) {
        optionValue.label = DEFAULT_FILTER_NAME;
        FILTER_ALL_OPEN = optionValue;
        return;
      }
      findingsFilterData.push(optionValue);
    }
  });
  findingsFilterData = (
    [FILTER_ALL_OPEN] as Array<FilterListOption | null>
  ).concat(findingsFilterData);

  return [findingsFilterData, FILTER_ALL_OPEN];
};

// Returns null only when the hierarchy is absent, unparseable, or not an array,
// so callers can tell "parsed to an empty tree" from "could not be read".
export const parseScopeHierarchy = (
  rawHierarchy: string | null | undefined
): ScopeNode[] | null => {
  if (!rawHierarchy) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawHierarchy);
    return Array.isArray(parsed) ? (parsed as ScopeNode[]) : null;
  } catch {
    return null;
  }
};

export const scopeHierarchyToRoot = (
  scopes: ScopeNode[]
): SelectedScope | null => {
  const root = scopes?.[0];

  if (!root) {
    return null;
  }

  return {
    label: root?.name,
    level: 0,
    value: {
      id: root?.id,
      editable: root?.editable,
      // resourceTypes.ROOT is the string "root"; ScopeType is the matching
      // string-literal union.
      type: resourceTypes.ROOT as ScopeType,
    },
  };
};

// Strips the unscoped node (only ever a direct child of the root) for pages that must not show it.
export const stripUnscopedNode = (scopes: ScopeNode[]): ScopeNode[] => {
  const root = scopes?.[0];

  if (!root?.children?.length) {
    return scopes;
  }

  const children = root.children.filter(
    (child) => child?.type !== resourceTypes.UNSCOPED
  );

  if (children.length === root.children.length) {
    return scopes;
  }

  return [{ ...root, children }, ...scopes.slice(1)];
};

export const readOnlyFields = ["finding_id", "id"];

export const nonSelectableScopesFields = ["resource_id"];

export const getSeverityFilter = (
  severity: string | undefined
): { filterPanels: FilterPanel[] } => {
  return {
    filterPanels: [
      {
        conditions: [
          {
            field: "remediation_item.actual_status",
            condition: "in",
            value: [
              {
                type: "category",
                key: statusCategories.OPEN,
                id: statusCategories.OPEN,
              },
            ],
          },
          {
            // Callers always pass a defined severity here; cast preserves the
            // exact runtime array while satisfying FilterItem.value's string[].
            value: [severity] as string[],
            field: "remediation_item.severity",
            condition: "in",
          },
        ],
      },
    ],
  };
};
